import type Organization from '@/services/organization';

export interface OrgAIContextSnapshot {
  // Legacy single-string fields. Kept for backward compat — readers should
  // prefer `industries` / the structured business fields below if present.
  industry?: string | null;
  description?: string | null;
  // Legacy structured identity fields (superseded by the question set below
  // but still emitted as a fallback when populated on older records).
  business?: string | null;
  customers?: string | null;
  differentiators?: string | null;
  objectives?: string | null;
  // Active identity fields (the question set surfaced in the form).
  industries?: Array<string | null> | null;
  productsServices?: string | null;
  companyType?: string | null;
  employeeCount?: string | null;
  mission?: string | null;
  foundedDate?: string | null;
  markets?: string | null;
  website?: string | null;
  // Brand action colors
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  // Surface colors
  backgroundColor?: string | null;
  surfaceColor?: string | null;
  borderColor?: string | null;
  // Text colors
  textColor?: string | null;
  mutedTextColor?: string | null;
  // Status colors
  successColor?: string | null;
  warningColor?: string | null;
  errorColor?: string | null;
  additionalInstructions?: string | null;
  logoSignedUrl?: string | null;
  iconSignedUrl?: string | null;
  logoDarkSignedUrl?: string | null;
  iconDarkSignedUrl?: string | null;
}

const VERSION = '2';

// BEEM brand defaults — used as fallbacks both in the form (visual prefill)
// and in the rendered AI context block, so an org that hasn't customized its
// palette still hands the model a coherent set of brand tokens to render with.
export const BEEM_COLOR_DEFAULTS = {
  primaryColor: '#0067F4',
  secondaryColor: '#755AFE',
  accentColor: '#97D7FE',
  backgroundColor: '#F8FAFF',
  surfaceColor: '#FFFFFF',
  borderColor: '#DADEE6',
  textColor: '#0A132B',
  mutedTextColor: '#4A5568',
  successColor: '#10B981',
  warningColor: '#FFD93D',
  errorColor: '#EF4444',
} as const;

// A theme's chart ("Rainbow") palette is a FIXED set of swatches, seeded from
// the theme's own brand colors so categorical charts read on-brand. The gen
// pipeline derives extra colours from these swatches + the primary when a chart
// needs more series than the palette holds.
export const CHART_PALETTE_SIZE = 6;

function _normHex(c: string | null | undefined): string | null {
  if (typeof c !== 'string') return null;
  const m = c.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return null;
  return `#${m.toLowerCase()}`;
}
function _hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace('#', '');
  return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
}
function _rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === rr) h = ((gg - bb) / d) % 6;
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s, l];
}
function _hslToHex(h: number, s: number, l: number): string {
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 60) { r = c; g = x; } else if (hh < 120) { r = x; g = c; } else if (hh < 180) { g = c; b = x; } else if (hh < 240) { g = x; b = c; } else if (hh < 300) { r = x; b = c; } else { r = c; b = x; }
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}
const _clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// Build a theme's default chart palette: the brand action colors (primary,
// secondary, accent) first, then harmonized hue-rotated variants of the primary
// spread around the wheel so the swatches stay distinct yet on-brand. Always
// returns exactly CHART_PALETTE_SIZE hex colors.
export function defaultChartPalette(
  colors: Partial<Record<'primaryColor' | 'secondaryColor' | 'accentColor', string | null>>,
): string[] {
  const seeds = [colors.primaryColor, colors.secondaryColor, colors.accentColor]
    .map((c) => _normHex(c))
    .filter((c): c is string => !!c);
  const hueOf = (hex: string) => {
    const [r, g, b] = _hexToRgb(hex);
    return _rgbToHsl(r, g, b)[0];
  };
  const out: string[] = [];
  const tooClose = (hex: string) =>
    out.some((o) => {
      const d = Math.abs(hueOf(o) - hueOf(hex));
      return Math.min(d, 360 - d) < 16;
    });
  // Keep the explicit brand colors verbatim (dedupe exact repeats only) — they
  // were chosen deliberately. Only the generated fillers below are hue-deduped.
  seeds.forEach((s) => { if (!out.includes(s)) out.push(s); });
  const primary = _normHex(out[0]) || _normHex(BEEM_COLOR_DEFAULTS.primaryColor) || '#0067f4';
  if (out.length === 0) out.push(primary); // no valid brand colors → anchor on the primary
  const [pr, pg, pb] = _hexToRgb(primary);
  const [ph, ps, pl] = _rgbToHsl(pr, pg, pb);
  const fillS = _clamp(ps || 0.6, 0.45, 0.7);
  const fillL = _clamp(pl || 0.5, 0.44, 0.58);
  let k = 1;
  while (out.length < CHART_PALETTE_SIZE && k < 36) {
    const cand = _hslToHex(ph + k * (360 / CHART_PALETTE_SIZE), fillS, fillL);
    if (!tooClose(cand)) out.push(cand);
    k += 1;
  }
  while (out.length < CHART_PALETTE_SIZE) {
    out.push(_hslToHex(ph + out.length * 47, fillS, fillL));
  }
  return out.slice(0, CHART_PALETTE_SIZE);
}

function escapeForBlock(s: string): string {
  // Strip closing tags that could break the surrounding block parser, including
  // the nested <additional_instructions> wrapper.
  return s
    .replace(/<\/organization_context>/gi, '')
    .replace(/<\/additional_instructions>/gi, '');
}

function line(label: string, value: string | null | undefined): string | null {
  if (!value || !value.trim()) return null;
  return `${label}: ${escapeForBlock(value.trim())}`;
}

function joinPalette(pairs: Array<[string, string | null | undefined]>): string | null {
  const filtered = pairs.filter(([, v]) => Boolean(v && v.trim())) as Array<[string, string]>;
  if (filtered.length === 0) return null;
  return filtered.map(([k, v]) => `${k} ${v.trim()}`).join(', ');
}

export function renderContextBlock(
  ctx: OrgAIContextSnapshot,
  org: Pick<Organization, 'name' | 'color'>,
): string {
  const lines: string[] = [
    `<organization_context version="${VERSION}">`,
    `You are assisting users at "${escapeForBlock(org.name)}".`,
  ];

  const industriesList =
    ctx.industries && ctx.industries.length
      ? ctx.industries.filter((s) => s && s.trim()).join(', ')
      : ctx.industry ?? '';
  const industry = line('Industry', industriesList);
  if (industry) lines.push(industry);

  const productsServices = line('Products and services', ctx.productsServices);
  if (productsServices) lines.push(productsServices);

  const companyType = line('Company type', ctx.companyType);
  if (companyType) lines.push(companyType);

  const employeeCount = line('Number of employees', ctx.employeeCount);
  if (employeeCount) lines.push(employeeCount);

  const mission = line('Mission or vision', ctx.mission);
  if (mission) lines.push(mission);

  const foundedDate = line('Founded', ctx.foundedDate);
  if (foundedDate) lines.push(foundedDate);

  const markets = line('Markets served or target expansion', ctx.markets);
  if (markets) lines.push(markets);

  // Legacy free-text fields (description + business/customers/differentiators
  // + objectives) are emitted whenever set, additive to the new question
  // fields above. The new form doesn't expose them, but rich text already
  // saved against the prior schema is meaningful context and must not be
  // dropped silently when a user fills in a single new field.
  const objectives = line('Main business objectives', ctx.objectives);
  if (objectives) lines.push(objectives);

  const business = line('What the business does', ctx.business);
  if (business) lines.push(business);

  const customers = line('Who the business serves', ctx.customers);
  if (customers) lines.push(customers);

  const differentiators = line('What sets the business apart', ctx.differentiators);
  if (differentiators) lines.push(differentiators);

  if (!objectives && !business && !customers && !differentiators) {
    const description = line('Description', ctx.description);
    if (description) lines.push(description);
  }

  const website = line('Website', ctx.website);
  if (website) lines.push(website);

  // Render colors as a structured palette so AI can pick the right token
  // for each UI surface (background, text, action, status). Each token
  // falls back to the BEEM brand default when the org hasn't customized it.
  const action = joinPalette([
    ['primary', ctx.primaryColor || BEEM_COLOR_DEFAULTS.primaryColor],
    ['secondary', ctx.secondaryColor || BEEM_COLOR_DEFAULTS.secondaryColor],
    ['accent', ctx.accentColor || BEEM_COLOR_DEFAULTS.accentColor],
  ]);
  const surfaces = joinPalette([
    ['background', ctx.backgroundColor || BEEM_COLOR_DEFAULTS.backgroundColor],
    ['surface', ctx.surfaceColor || BEEM_COLOR_DEFAULTS.surfaceColor],
    ['border', ctx.borderColor || BEEM_COLOR_DEFAULTS.borderColor],
  ]);
  const text = joinPalette([
    ['primary text', ctx.textColor || BEEM_COLOR_DEFAULTS.textColor],
    ['muted text', ctx.mutedTextColor || BEEM_COLOR_DEFAULTS.mutedTextColor],
  ]);
  const status = joinPalette([
    ['success', ctx.successColor || BEEM_COLOR_DEFAULTS.successColor],
    ['warning', ctx.warningColor || BEEM_COLOR_DEFAULTS.warningColor],
    ['error', ctx.errorColor || BEEM_COLOR_DEFAULTS.errorColor],
  ]);

  if (action || surfaces || text || status) {
    lines.push('Brand colors (use these tokens for any AI-generated UI / artifact):');
    if (action) lines.push(`  Action: ${action}`);
    if (surfaces) lines.push(`  Surfaces: ${surfaces}`);
    if (text) lines.push(`  Text: ${text}`);
    if (status) lines.push(`  Status: ${status}`);
  }

  const assetBits: string[] = [];
  if (ctx.logoSignedUrl) assetBits.push(`  - logo (light surfaces): ${ctx.logoSignedUrl}`);
  if (ctx.logoDarkSignedUrl) assetBits.push(`  - logo (dark surfaces): ${ctx.logoDarkSignedUrl}`);
  if (ctx.iconSignedUrl) assetBits.push(`  - icon (light surfaces): ${ctx.iconSignedUrl}`);
  if (ctx.iconDarkSignedUrl) assetBits.push(`  - icon (dark surfaces): ${ctx.iconDarkSignedUrl}`);
  if (assetBits.length) {
    lines.push('Brand assets available for inclusion in artifacts:');
    lines.push(...assetBits);
  }

  if (ctx.additionalInstructions && ctx.additionalInstructions.trim()) {
    lines.push('<additional_instructions>');
    lines.push(`  ${escapeForBlock(ctx.additionalInstructions.trim())}`);
    lines.push('</additional_instructions>');
  }

  lines.push('</organization_context>');
  return lines.join('\n');
}

export function isContextMeaningful(ctx: OrgAIContextSnapshot): boolean {
  return Boolean(
    ctx.industry ||
      ctx.description ||
      (ctx.industries && ctx.industries.length) ||
      ctx.business ||
      ctx.customers ||
      ctx.mission ||
      ctx.differentiators ||
      ctx.objectives ||
      ctx.productsServices ||
      ctx.companyType ||
      ctx.employeeCount ||
      ctx.foundedDate ||
      ctx.markets ||
      ctx.website ||
      ctx.additionalInstructions ||
      ctx.primaryColor ||
      ctx.secondaryColor ||
      ctx.accentColor ||
      ctx.backgroundColor ||
      ctx.surfaceColor ||
      ctx.borderColor ||
      ctx.textColor ||
      ctx.mutedTextColor ||
      ctx.successColor ||
      ctx.warningColor ||
      ctx.errorColor ||
      ctx.logoSignedUrl ||
      ctx.iconSignedUrl ||
      ctx.logoDarkSignedUrl ||
      ctx.iconDarkSignedUrl,
  );
}
